import {
  Alert,
  Flex,
  IconClockLine,
  IconInfoSolid,
  List,
  Pill,
  Tabs,
  Text,
  ToggleDetails,
  ToggleGroup,
  Tooltip,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useState } from "react";
import { Actions } from "../../../../components/Action/Actions";
import { Card } from "../../../../components/Card";
import {
  getFutureVersions,
  getVersionByDate,
  isArchived,
  sortOutcomes,
} from "../../../../components/LearningOutcomes/utils";
import { ContextReflections } from "../../../../components/Reflection/ContextReflections";
import ChartData from "../../../../sections/ChartData";
import { TermsContext } from "../../../../utils/Context";

const CourseView = ({
  currentSection,
  dashboardFilters,
  setDashboardFilters,
  trigger,
  setTrigger,
  chartLoad,
  setChartLoad,
}) => {
  const { insightsTerms } = useContext(TermsContext);
  const [tabIndex, setTabIndex] = useState(0);
  const [outcomeTabIndex, setOutcomeTabIndex] = useState(0);

  const handleFacultyTabChange = (e, { index }) => {
    setTabIndex(index);
  };

  const handleOutcomeTabChange = (e, { index }) => {
    setOutcomeTabIndex(index);
  };

  const findInsightsTerm = (canvasTermId) => {
    let term;
    insightsTerms.forEach((insightsTerm) => {
      insightsTerm.canvas_terms.forEach((canvasTerm) => {
        if (canvasTerm.id === canvasTermId) {
          term = insightsTerm;
        }
      });
    });
    return term;
  };

  return (
    <Flex
      alignItems="start"
      wrap="wrap"
      gap="small"
      width="100%"
      justifyItems="space-between"
    >
      <Flex.Item shouldGrow size="100%">
        <ChartData
          showLineChart
          showBarChart={false}
          inCourse
          isFacultyDash
          filters={dashboardFilters}
          setFilters={setDashboardFilters}
          trigger={trigger}
          setTrigger={setTrigger}
          section={currentSection}
          chartLoad={chartLoad}
          setChartLoad={setChartLoad}
          courseID={currentSection.canvas_id}
        />
      </Flex.Item>
      <Flex.Item size="100%" margin="small 0 0 0">
        <Card style={{ height: 300, position: "relative" }}>
          <Flex justifyItems="space-between" gap="small" alignItems="start">
            <Flex.Item shouldShrink shouldGrow size="48%">
              <Tabs onRequestTabChange={handleOutcomeTabChange} maxHeight={250}>
                <Tabs.Panel
                  id="approved-outcomes-faculty"
                  renderTitle={t("dashboard.approvedOutcomes")}
                  isSelected={outcomeTabIndex === 0}
                >
                  <View position="relative" tabIndex="0" role="button">
                    <List isUnstyled>
                      {currentSection?.learning_outcomes?.length > 0 ? (
                        sortOutcomes(currentSection.learning_outcomes)
                          .filter((outcome) => !isArchived(outcome))
                          .map((outcome, key) => {
                            const version = getVersionByDate(
                              outcome,
                              new Date(currentSection.term.start_at)
                            );
                            const futureVersions = getFutureVersions(outcome);
                            return (
                              <List.Item key={key}>
                                <div
                                  style={{
                                    color: "#0D323F",
                                    backgroundColor: "#F2F8FA",
                                    border: "1px solid #333",
                                    borderRadius: "10px",
                                    padding: "5px 15px",
                                    marginBottom: 10,
                                  }}
                                >
                                  {version?.variants?.length > 0 &&
                                  new Date(
                                    version.variants.sort(
                                      (a, b) =>
                                        new Date(b.last_updated) -
                                        new Date(a.last_updated)
                                    )[0].last_updated
                                  ) > new Date(version.last_updated) ? (
                                    <Text size="small">
                                      {version.variants[0].name && (
                                        <Text size="small" weight="bold">
                                          {version.variants[0].name}:{" "}
                                        </Text>
                                      )}
                                      {version.variants[0].statement}{" "}
                                      <Pill color="info">
                                        {t("dashboard.myOutcomeVariant")}
                                      </Pill>
                                      <ToggleDetails
                                        size="small"
                                        summary={
                                          <Flex justifyItems="space-between">
                                            <Flex.Item>
                                              {t(
                                                "dashboard.viewCurrentOutcomeVersion"
                                              )}
                                              <Tooltip
                                                renderTip={
                                                  <div
                                                    style={{ width: "600px" }}
                                                  >
                                                    {t(
                                                      "dashboard.currentVersionResultsInfo"
                                                    )}
                                                  </div>
                                                }
                                                placement="end"
                                                on={["click", "hover", "focus"]}
                                                color="primary-inverse"
                                              >
                                                <IconInfoSolid color="brand" />
                                              </Tooltip>
                                            </Flex.Item>
                                          </Flex>
                                        }
                                      >
                                        <Text size="small">
                                          {version.name && (
                                            <Text size="small" weight="bold">
                                              {version.name}:{" "}
                                            </Text>
                                          )}
                                          {version.statement}
                                        </Text>
                                      </ToggleDetails>
                                    </Text>
                                  ) : (
                                    <Text size="small">
                                      {version.name && (
                                        <Text size="small" weight="bold">
                                          {version.name}:{" "}
                                        </Text>
                                      )}
                                      {version.statement}
                                    </Text>
                                  )}
                                  {futureVersions.length > 0 && (
                                    <Tooltip
                                      color="primary-inverse"
                                      renderTip={
                                        <div style={{ maxWidth: 250 }}>
                                          {futureVersions.map(
                                            (futureVersion, key) => (
                                              <Text size="small" key={key}>
                                                <Text
                                                  weight="bold"
                                                  size="small"
                                                >
                                                  {
                                                    findInsightsTerm(
                                                      futureVersion
                                                        ?.effective_term?.ddl_id
                                                    )?.name
                                                  }
                                                  :{" "}
                                                </Text>
                                                {futureVersion.statement}
                                                <br />
                                              </Text>
                                            )
                                          )}
                                        </div>
                                      }
                                    >
                                      <Pill
                                        color="info"
                                        renderIcon={<IconClockLine />}
                                        margin="0 0 0 x-small"
                                      >
                                        {t("learningOutcomes.upcoming")}
                                      </Pill>
                                    </Tooltip>
                                  )}
                                </div>
                              </List.Item>
                            );
                          })
                      ) : (
                        <List.Item>
                          <Alert>{t("dashboard.noApprovedOutcomes")}</Alert>
                        </List.Item>
                      )}
                    </List>
                    {currentSection?.learning_outcomes?.filter((outcome) =>
                      isArchived(outcome)
                    ).length > 0 && (
                      <ToggleGroup
                        toggleLabel={t("learningOutcomes.archive.archivedCLOs")}
                        summary={t("learningOutcomes.archive.archivedCLOs")}
                      >
                        <List isUnstyled>
                          {currentSection.learning_outcomes
                            .filter((outcome) => isArchived(outcome))
                            .map((outcome, key) => (
                              <List.Item key={key}>
                                <div
                                  style={{
                                    color: "#0D323F",
                                    backgroundColor: "#F2F8FA",
                                    border: "1px solid #333",
                                    borderRadius: "10px",
                                    padding: "5px 15px",
                                    margin: 15,
                                  }}
                                >
                                  <Text size="small">
                                    {outcome.name && (
                                      <Text size="small" weight="bold">
                                        {outcome.name}:{" "}
                                      </Text>
                                    )}
                                    {outcome.statement}{" "}
                                    <Pill>
                                      {t("learningOutcomes.archive.archived")}{" "}
                                      {outcome.archive_term.insights_term_id}
                                    </Pill>
                                  </Text>
                                </div>
                              </List.Item>
                            ))}
                        </List>
                      </ToggleGroup>
                    )}
                  </View>
                </Tabs.Panel>
                <Tabs.Panel
                  id="unapproved-outcomes-faculty"
                  renderTitle={t("dashboard.unapprovedOutcomes")}
                  isSelected={outcomeTabIndex === 1}
                >
                  <View position="relative" tabIndex="0" role="button">
                    <div
                      style={{
                        border: "1px solid #89CFF0",
                        padding: 10,
                        borderRadius: "5px",
                      }}
                    >
                      <Text>{t("dashboard.unapprovedDisclaimer")}</Text>
                    </div>
                    <List isUnstyled>
                      {currentSection?.unapproved_outcomes?.length > 0 ? (
                        currentSection.unapproved_outcomes.map(
                          (outcome, key) => (
                            <List.Item key={key} margin="small 0">
                              <div
                                style={{
                                  color: "#0D323F",
                                  backgroundColor: "#FFD580",
                                  border: "1px solid #333",
                                  borderRadius: "10px",
                                  padding: "5px 15px",
                                  marginBottom: 10,
                                }}
                              >
                                <Text size="small">
                                  {outcome.name && (
                                    <Text size="small" weight="bold">
                                      {outcome.name}:{" "}
                                    </Text>
                                  )}
                                  {outcome.statement}
                                </Text>
                              </div>
                            </List.Item>
                          )
                        )
                      ) : (
                        <List.Item>
                          <Alert>{t("dashboard.noUnapprovedOutcomes")}</Alert>
                        </List.Item>
                      )}
                    </List>
                  </View>
                </Tabs.Panel>
              </Tabs>
            </Flex.Item>
            <Flex.Item>
              <div
                style={{
                  display: "block",
                  height: 300,
                  borderRight: "1px solid #ccc",
                }}
              />
            </Flex.Item>
            <Flex.Item shouldShrink shouldGrow size="48%">
              <Tabs onRequestTabChange={handleFacultyTabChange}>
                <Tabs.Panel
                  id="reflections-faculty"
                  renderTitle={t("reflections.reflections")}
                  isSelected={tabIndex === 0}
                  padding="small 0"
                >
                  <div
                    style={{
                      maxHeight: 225,
                      overflowY: "auto",
                      overflowX: "hidden",
                      padding: "0 10px",
                    }}
                  >
                    <ContextReflections
                      contextId={currentSection.id}
                      contextName={currentSection.name}
                    />
                  </div>
                </Tabs.Panel>
                <Tabs.Panel
                  id="actions-faculty"
                  renderTitle={t("actions.actions")}
                  isSelected={tabIndex === 1}
                  padding="small 0"
                >
                  <div
                    style={{
                      maxHeight: 225,
                      overflowY: "auto",
                      overflowX: "hidden",
                      padding: "0 10px",
                    }}
                  >
                    <Actions contextId={currentSection.id} />
                  </div>
                </Tabs.Panel>
              </Tabs>
            </Flex.Item>
          </Flex>
        </Card>
      </Flex.Item>
    </Flex>
  );
};

export default CourseView;
